<template>
  <NavBar pageName="Euro/RON Exchange Rates"></NavBar>
  <v-container style="margin-top: 185px;">
    <v-btn variant="text" size="small" @click="$router.push('/settings')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Settings
    </v-btn>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ t.settings?.euro_ron_rates || 'Euro/RON Exchange Rates' }}</span>
        <v-btn color="success" size="small" @click="openAddDialog">
          <v-icon class="mr-1">mdi-plus</v-icon>
          {{ t.common?.add || 'Add' }}
        </v-btn>
      </v-card-title>

      <!-- Current Rate Info -->
      <v-card-text v-if="currentRate" class="pb-0">
        <v-alert type="info" density="compact" variant="tonal">
          <div class="d-flex justify-space-between align-center">
            <span>
              <strong>{{ t.settings?.current_rate || 'Current Rate' }}:</strong>
              1 EUR = <strong>{{ currentRate.euro_ron_rate }}</strong> RON
              ({{ formatDate(currentRate.rate_date) }})
            </span>
            <v-switch
              v-model="showTerminated"
              :label="t.settings?.show_terminated || 'Show Terminated'"
              density="compact"
              hide-details
              class="ml-4"
              @update:model-value="loadRates"
            ></v-switch>
          </div>
        </v-alert>
      </v-card-text>

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="rates"
          :loading="loading"
          :items-per-page="20"
          class="elevation-1"
        >
          <template v-slot:[`item.euro_ron_rate`]="{ item }">
            <strong>{{ item.euro_ron_rate.toFixed(4) }}</strong>
          </template>

          <template v-slot:[`item.rate_date`]="{ item }">
            {{ formatDate(item.rate_date) }}
          </template>

          <template v-slot:[`item.source`]="{ item }">
            <v-chip
              :color="item.source === 'bnr' ? 'primary' : 'secondary'"
              size="small"
            >
              {{ item.source === 'bnr' ? 'BNR' : 'Manual' }}
            </v-chip>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="item.status === 'active' ? 'success' : 'grey'"
              size="small"
            >
              {{ item.status === 'active' ? (t.common?.active || 'Active') : (t.common?.terminated || 'Terminated') }}
            </v-chip>
          </template>

          <template v-slot:[`item.used_by_orders`]="{ item }">
            <v-chip
              :color="item.used_by_orders ? 'warning' : 'success'"
              size="small"
            >
              {{ item.used_by_orders ? (t.common?.yes || 'Yes') : (t.common?.no || 'No') }}
            </v-chip>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              size="small"
              variant="text"
              color="primary"
              @click="viewRate(item)"
              :title="t.common?.view || 'View'"
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Add Rate Dialog -->
    <v-dialog v-model="showDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          {{ t.settings?.add_rate || 'Add Exchange Rate' }}
        </v-card-title>

        <v-card-text>
          <v-alert type="info" density="compact" class="mb-4">
            {{ t.settings?.rate_info || 'Add the current EUR/RON exchange rate. This will be used for all new orders requiring currency conversion.' }}
          </v-alert>

          <v-text-field
            v-model="formData.rate_date"
            :label="t.common?.date || 'Date'"
            type="date"
            variant="outlined"
            density="compact"
            :rules="[v => !!v || 'Date is required']"
            class="mb-3"
          ></v-text-field>

          <v-text-field
            v-model.number="formData.euro_ron_rate"
            :label="t.settings?.exchange_rate || 'Exchange Rate (1 EUR = ? RON)'"
            variant="outlined"
            density="compact"
            type="number"
            step="0.0001"
            min="0"
            :rules="[v => v > 0 || 'Rate must be positive']"
            class="mb-3"
          >
            <template v-slot:append-inner>
              <span class="text-grey">RON</span>
            </template>
          </v-text-field>

          <v-textarea
            v-model="formData.notes"
            :label="t.common?.notes || 'Notes'"
            variant="outlined"
            density="compact"
            rows="2"
          ></v-textarea>

          <!-- Conversion Preview -->
          <v-card v-if="formData.euro_ron_rate > 0" variant="tonal" class="pa-3 mt-3">
            <div class="text-subtitle-2 mb-2">{{ t.settings?.conversion_preview || 'Conversion Preview' }}</div>
            <div class="d-flex justify-space-between">
              <span>10 EUR =</span>
              <strong>{{ (10 * formData.euro_ron_rate).toFixed(2) }} RON</strong>
            </div>
            <div class="d-flex justify-space-between">
              <span>100 EUR =</span>
              <strong>{{ (100 * formData.euro_ron_rate).toFixed(2) }} RON</strong>
            </div>
          </v-card>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">{{ t.common?.cancel || 'Cancel' }}</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="saveRate"
            :loading="saving"
            :disabled="!formData.rate_date || !formData.euro_ron_rate || formData.euro_ron_rate <= 0"
          >
            {{ t.common?.save || 'Save' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Rate Dialog -->
    <v-dialog v-model="showViewDialog" max-width="400px">
      <v-card v-if="viewingRate">
        <v-card-title>{{ t.settings?.rate_details || 'Rate Details' }}</v-card-title>
        <v-card-text>
          <div class="mb-2">
            <strong>{{ t.common?.date || 'Date' }}:</strong> {{ formatDate(viewingRate.rate_date) }}
          </div>
          <div class="mb-2">
            <strong>{{ t.settings?.exchange_rate || 'Rate' }}:</strong> 1 EUR = {{ viewingRate.euro_ron_rate.toFixed(4) }} RON
          </div>
          <div class="mb-2">
            <strong>{{ t.common?.source || 'Source' }}:</strong> {{ viewingRate.source === 'bnr' ? 'BNR' : 'Manual' }}
          </div>
          <div class="mb-2">
            <strong>{{ t.common?.status || 'Status' }}:</strong>
            <v-chip :color="viewingRate.status === 'active' ? 'success' : 'grey'" size="small" class="ml-2">
              {{ viewingRate.status === 'active' ? (t.common?.active || 'Active') : (t.common?.terminated || 'Terminated') }}
            </v-chip>
          </div>
          <div class="mb-2" v-if="viewingRate.notes">
            <strong>{{ t.common?.notes || 'Notes' }}:</strong> {{ viewingRate.notes }}
          </div>
          <div class="mb-2">
            <strong>{{ t.settings?.used_by_orders || 'Used by Orders' }}:</strong>
            <v-chip :color="viewingRate.used_by_orders ? 'warning' : 'success'" size="small" class="ml-2">
              {{ viewingRate.used_by_orders ? (t.common?.yes || 'Yes') : (t.common?.no || 'No') }}
            </v-chip>
          </div>
          <div>
            <strong>{{ t.common?.created_at || 'Created' }}:</strong> {{ formatDateTime(viewingRate.created_at) }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showViewDialog = false">{{ t.common?.close || 'Close' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import store from "@/store";
import { log } from "@/composables/useLog";
import { getEuroRonRates, saveEuroRonRate as saveEuroRonRateAPI } from "@/composables/useEuroRonRateAPI";
import { t as tRef } from "@/composables/useLanguages";

export default defineComponent({
  name: 'EuroRonRatesSettings',
  components: {
    NavBar
  },
  data() {
    return {
      loading: false,
      saving: false,
      rates: [],
      currentRate: null,
      showDialog: false,
      showViewDialog: false,
      viewingRate: null,
      formData: {
        rate_date: new Date().toISOString().split('T')[0],
        euro_ron_rate: null,
        notes: ''
      },
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      showTerminated: false
    }
  },
  computed: {
    t() {
      return tRef.value;
    },
    token() {
      return store.getters.getToken;
    },
    headers() {
      return [
        { title: 'ID', key: 'id', width: '60px' },
        { title: this.t.common?.date || 'Date', key: 'rate_date', width: '120px' },
        { title: this.t.settings?.exchange_rate || 'Rate (EUR/RON)', key: 'euro_ron_rate', width: '150px' },
        { title: this.t.common?.source || 'Source', key: 'source', width: '100px' },
        { title: this.t.common?.status || 'Status', key: 'status', width: '100px' },
        { title: this.t.settings?.used_by_orders || 'Used', key: 'used_by_orders', width: '100px' },
        { title: this.t.common?.notes || 'Notes', key: 'notes' },
        { title: this.t.common?.created_at || 'Created', key: 'created_at', width: '150px' },
        { title: this.t.common?.actions || 'Actions', key: 'actions', sortable: false, width: '80px' }
      ];
    }
  },
  methods: {
    async loadRates() {
      this.loading = true;
      try {
        const response = await getEuroRonRates(this.token, { include_terminated: this.showTerminated });
        if (!response.error) {
          this.rates = response.rates || [];
          this.currentRate = response.current_rate;
        } else {
          this.showMessage(response.message || 'Error loading rates', 'error');
        }
      } catch (err) {
        log("Error loading rates:", err);
        this.showMessage('Error loading rates', 'error');
      } finally {
        this.loading = false;
      }
    },

    openAddDialog() {
      this.formData = {
        rate_date: new Date().toISOString().split('T')[0],
        euro_ron_rate: this.currentRate?.euro_ron_rate || null,
        notes: ''
      };
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
    },

    async saveRate() {
      this.saving = true;
      try {
        const response = await saveEuroRonRateAPI(this.token, this.formData);
        if (!response.error) {
          this.showMessage(response.message || 'Rate saved', 'success');
          this.closeDialog();
          await this.loadRates();
        } else {
          this.showMessage(response.message || 'Error saving rate', 'error');
        }
      } catch (err) {
        log("Error saving rate:", err);
        this.showMessage('Error saving rate', 'error');
      } finally {
        this.saving = false;
      }
    },

    viewRate(rate) {
      this.viewingRate = rate;
      this.showViewDialog = true;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('ro-RO');
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('ro-RO') + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    showMessage(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
    }
  },
  mounted() {
    this.loadRates();
  }
});
</script>
